import i18n, { type Resource } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { DateTime, Settings } from "luxon";
import moment from "moment";
// @ts-expect-error side-effect import
import "moment/dist/locale/de";
import { initReactI18next } from "react-i18next";
import de from "./res/default.de";
import en from "./res/default.en";
import notificationsDe from "./res/notifications.de.json";
import notificationsEn from "./res/notifications.en.json";
import { SUPPORTED_PORTAL_LANGUAGE } from "./supportedLanguages";

const defaultResources = SUPPORTED_PORTAL_LANGUAGE.reduce((acc, lang) => {
	acc[lang] = {
		default: { foo: "bar" },
	};
	return acc;
}, {} as Resource);
i18n.use(LanguageDetector).use(initReactI18next);

function formatWithLuxon(
	date: DateTime | string,
	format: any,
	lng: string,
): string {
	const useTZ = typeof format === "string" && format.includes("+useTZ");
	const normalizedFormat = useTZ ? format.replace("+useTZ", "") : format;
	const d =
		typeof date === "string"
			? DateTime.fromISO(date, { setZone: useTZ })
			: date;
	if (typeof normalizedFormat === "string") {
		return d.setLocale(lng).toFormat(normalizedFormat);
	}
	return d.toLocaleString(normalizedFormat, {
		locale: lng,
	});
}

/**
 * Custom catch-all format function dispatching by value type.
 * Registered on the interpolator after init since i18next v26
 * removed the `interpolation.format` option.
 */
export function formatValue(value: any, format: any, lng: any): string {
	if (format === "uppercase") return value.toUpperCase();
	if (DateTime.isDateTime(value)) {
		return formatWithLuxon(value, format, lng);
	}
	if (value instanceof Date) {
		return formatWithLuxon(DateTime.fromJSDate(value), format, lng);
	}
	if (typeof value === "string") {
		return formatWithLuxon(value, format, lng);
	}
	if (moment.isMoment(value)) {
		return formatWithLuxon(
			DateTime.fromJSDate(value.toDate()),
			format,
			lng,
		);
	}
	return `UNKOWN value: ${value}`;
}

i18n.init(
	{
		react: {
			useSuspense: false,
		},
		resources: {
			// ! empty default resources needed for "i18n.resolvedLanguage"
			...defaultResources,
			// !overwrite default resources with resources we have translations for
			en: { default: en, notifications: notificationsEn },
			de: { default: de, notifications: notificationsDe },
		},
		fallbackLng: ["en", "de"],
		debug: false,
		interpolation: {
			escapeValue: false, // not needed for react
			formatSeparator: ",",
		},
	},
	() => {
		Settings.defaultLocale = i18n.resolvedLanguage ?? "en";

		// Migrated from interpolation.format (removed in i18next v26).
		// Override the interpolator's format method directly since the
		// Formatter service only handles named formatters, not dynamic
		// format strings like Luxon date patterns.
		// biome-ignore lint/suspicious/noExplicitAny: i18next v26 removed `format` from types but the interpolator still uses it
		(i18n.services.interpolator as any).format = formatValue;
	},
);

export default i18n;
